import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, Snackbar, TextField, Typography
} from '@mui/material';
import {
  Download as DownloadIcon, Description as DocIcon, CheckCircle as CheckIcon, Inventory2 as PackageIcon
} from '@mui/icons-material';
import deliveryService from '../../services/deliveryService';
import { clientEntityStatus, groupByCompany } from '../../utils/clientDeliveries';

const formatDateTime = (value) => (value ? new Date(value).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const errorMessage = (e, fallback) => e?.response?.data?.error || fallback;

const currentUserName = () => {
  try {
    const info = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
    return [info.firstName, info.firstLastName].filter(Boolean).join(' ');
  } catch (e) {
    return '';
  }
};

const AckDialog = ({ open, entity, onClose, onConfirm, busy }) => {
  const [checked, setChecked] = useState(false);
  const [name, setName] = useState(currentUserName());
  useEffect(() => { if (open) { setChecked(false); setName(currentUserName()); } }, [open]);
  if (!entity) return null;
  const items = entity.package?.items || [];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Confirmar recepción de documentos</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>{entity.entityName} · {items.length} documento{items.length === 1 ? '' : 's'}</Typography>
        <FormControlLabel sx={{ mt: 1, alignItems: 'flex-start' }}
          control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} sx={{ pt: 0 }} />}
          label="Recibí y revisé los documentos de mi Programa Interno de Protección Civil." />
        <TextField fullWidth margin="normal" label="Nombre de quien recibe" value={name} onChange={(e) => setName(e.target.value)} />
        <Typography variant="caption" color="text.secondary">Se guardará la fecha y hora de tu confirmación como acuse de recibo.</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" color="success" disabled={!checked || !name.trim() || busy} onClick={() => onConfirm(name.trim())}>
          {busy ? <CircularProgress size={18} color="inherit" /> : 'Confirmar recepción'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EntityCard = ({ entity, onDownload, onAck, downloadingId }) => {
  const status = clientEntityStatus(entity);
  const pkg = entity.package;
  const showDocs = pkg && (entity.stage === 'LIBERADA' || entity.stage === 'ENTREGADA');
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid #eee', flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {entity.entityName} <Chip size="small" label={entity.entityType === 'MATRIZ' ? 'Matriz' : 'Sucursal'} sx={{ ml: 0.5 }} />
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {pkg?.releasedAt ? `Entregado por CONEM el ${formatDateTime(pkg.releasedAt)}` : entity.orderNumber}
          </Typography>
        </Box>
        <Chip label={status.label} color={status.color} variant={status.color === 'default' ? 'outlined' : 'filled'} />
      </Box>
      <CardContent>
        {!showDocs && <Typography variant="body2" color="text.secondary">{status.description}</Typography>}
        {showDocs && (
          <>
            {pkg.message && <Alert severity="info" sx={{ mb: 1.5 }}>{pkg.message}</Alert>}
            {pkg.items.map((item) => (
              <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: '1px solid #f1f3f5' }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: '#e3f2fd', color: '#1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DocIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.version ? `Versión ${item.version}` : 'Documento'}{item.downloadedAt ? ` · descargado el ${formatDateTime(item.downloadedAt)}` : ''}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" startIcon={downloadingId === item.id ? <CircularProgress size={14} /> : <DownloadIcon />}
                  disabled={downloadingId === item.id} onClick={() => onDownload(entity, item)}>
                  Descargar
                </Button>
              </Box>
            ))}
            {entity.stage === 'LIBERADA' && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="contained" color="success" startIcon={<CheckIcon />} onClick={() => onAck(entity)}>Recibí de conformidad</Button>
              </Box>
            )}
            {entity.stage === 'ENTREGADA' && pkg.ackAt && (
              <Alert severity="success" icon={<CheckIcon />} sx={{ mt: 2 }}>
                {pkg.ackMethod === 'FISICO' ? 'Entrega física recibida' : 'Confirmaste la recepción'} el {formatDateTime(pkg.ackAt)}{pkg.ackReceivedBy ? ` (${pkg.ackReceivedBy})` : ''}.
              </Alert>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const MisDocumentosEntregados = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ackEntity, setAckEntity] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [snack, setSnack] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await deliveryService.mine());
      setError('');
    } catch (e) {
      setError(errorMessage(e, 'No se pudieron cargar tus documentos'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => groupByCompany(data?.entities || []), [data]);

  const handleDownload = async (entity, item) => {
    setDownloadingId(item.id);
    try {
      await deliveryService.download(entity.package.id, item);
      if (!item.downloadedAt) load();
    } catch (e) {
      setSnack({ severity: 'error', message: 'No se pudo descargar el documento. Intenta de nuevo o avisa a CONEM.' });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleConfirm = async (name) => {
    setBusy(true);
    try {
      await deliveryService.acknowledge(ackEntity.package.id, name);
      setAckEntity(null);
      setSnack({ severity: 'success', message: 'Gracias, registramos tu acuse de recibo.' });
      await load();
    } catch (e) {
      setSnack({ severity: 'error', message: errorMessage(e, 'No se pudo registrar tu confirmación') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ background: 'linear-gradient(135deg, #1976d2, #6a1b9a)', color: '#fff', borderRadius: 3, p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}><PackageIcon /> Mis documentos entregados</Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>{(data?.companies || []).join(' · ') || 'Programa Interno de Protección Civil'}</Typography>
      </Box>

      {loading && !data && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={load}>Reintentar</Button>}>{error}</Alert>}

      {data && data.pendingAcknowledgments > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <b>Tienes documentos nuevos.</b> Revísalos y confirma que los recibiste ({data.pendingAcknowledgments} pendiente{data.pendingAcknowledgments === 1 ? '' : 's'}).
        </Alert>
      )}
      {data && data.entities.length === 0 && (
        <Alert severity="info">Todavía no tienes servicios en proceso. Cuando CONEM entregue tus documentos aparecerán aquí.</Alert>
      )}

      {groups.map((group) => (
        <Box key={group.companyName} sx={{ mb: 2 }}>
          {groups.length > 1 && <><Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{group.companyName}</Typography><Divider sx={{ mb: 2 }} /></>}
          {group.entities.map((entity) => (
            <EntityCard key={entity.detailId} entity={entity} downloadingId={downloadingId} onDownload={handleDownload} onAck={setAckEntity} />
          ))}
        </Box>
      ))}

      <AckDialog open={Boolean(ackEntity)} entity={ackEntity} busy={busy} onClose={() => setAckEntity(null)} onConfirm={handleConfirm} />
      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(null)}>{snack.message}</Alert> : <span />}
      </Snackbar>
    </Container>
  );
};

export default MisDocumentosEntregados;
